import {
    wsconnect,
    NatsConnection,
    ConnectionOptions,
    jwtAuthenticator,
} from "@nats-io/nats-core";
import { useEffect, useMemo, useRef, useState } from "react";
import { useActiveWorkspaceDetails } from "@hooks/useActiveWorkspaceDetails";
import { useNatsJwt } from "@api/nats/nats.api";
import { NatsContext } from "./NatsContext";
import { NATS_SERVERS } from "./nats.lib";

interface NatsProviderProps {
    readonly children: React.ReactNode;
}

export const NatsProvider = ({ children }: NatsProviderProps) => {
    const [connection, setConnection] = useState<
        NatsConnection | null | undefined
    >(null);
    const connectionRef = useRef<NatsConnection | null>(null);
    const workspaceDetails = useActiveWorkspaceDetails();
    const {
        data: jwtData,
        error: jwtError,
        isLoading: isJwtLoading,
        refetch: refetchJwt,
    } = useNatsJwt();

    const jwt = jwtData?.jwt;
    const workspaceName = workspaceDetails?.workspace?.name;

    const options: ConnectionOptions = useMemo(
        () => ({
            servers: NATS_SERVERS,
            authenticator: jwt ? jwtAuthenticator(jwt) : undefined,
            name: workspaceName,
        }),
        [jwt, workspaceName],
    );

    useEffect(() => {
        let isCancelled = false;

        const setupConnection = async () => {
            if (!jwtData?.jwt || isJwtLoading || jwtError) {
                return;
            }

            if (connectionRef.current) {
                await connectionRef.current.close();
                connectionRef.current = null;
                setConnection(null);
            }

            const nc = await wsconnect(options);
            if (isCancelled) {
                await nc.close();
                return;
            }

            connectionRef.current = nc;
            setConnection(nc);

            const ncstatus = nc.status();
            for await (const status of ncstatus) {
                if (isCancelled) {
                    break;
                }

                if (
                    status.type === "error" &&
                    status.error.name === "UserAuthenticationExpiredError"
                ) {
                    void refetchJwt();
                    connectionRef.current = null;
                    setConnection(null);
                    await nc.close();
                    break;
                }
            }
        };

        setupConnection().catch((error) => {
            if (!isCancelled) {
                // eslint-disable-next-line no-console
                console.error(
                    "NatsProvider: failed to establish NATS connection",
                    error,
                );
            }
        });

        return () => {
            isCancelled = true;
            const currentConnection = connectionRef.current;
            connectionRef.current = null;
            setConnection(null);
            void currentConnection?.close();
        };
    }, [jwtData?.jwt, isJwtLoading, jwtError, refetchJwt, options]);

    return (
        <NatsContext.Provider value={connection}>
            {children}
        </NatsContext.Provider>
    );
};
